import { chakra } from "@chakra-ui/react"
import { type IntlShape, useIntl } from "react-intl"

import { type CurrencyCode, CurrencyCodes } from "@/domain/currency/constants"
import { type LocalRefLabel, get_t11n_label } from "@/utils/i18n/t11n"
import { isBlank, isNullOrUndefined } from "@/utils/ts-utils"

type Props = {
  price?: number | null
  currency?: LocalRefLabel | null
  dft?: string
}

const NBSP = " "

// Brand price format (ADR 0001, Q10): "1 250 000 €", no decimals, non-breaking spaces (Intl
// uses narrow ones in French).
export const formatPrice = (intl: IntlShape, price: number, currency?: LocalRefLabel | null) => {
  const currencyCode = (currency?.code ?? "xxx").toUpperCase()
  if (CurrencyCodes.includes(currencyCode as CurrencyCode)) {
    return intl
      .formatNumber(price, {
        style: "currency",
        currency: currencyCode,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })
      .replace(/[  ]/g, NBSP)
  }

  const localLabel = isNullOrUndefined(currency) ? "" : get_t11n_label(currency, intl)
  const amount = intl
    .formatNumber(price, { style: "decimal", maximumFractionDigits: 0 })
    .replace(/[  ]/g, NBSP)

  return isBlank(localLabel) ? amount : `${amount}${NBSP}${localLabel}`
}

export const FormattedPrice: React.FC<Props> = ({ price, currency, dft }: Props) => {
  const intl = useIntl()

  const empty = dft ?? null

  if (isNullOrUndefined(price) || isBlank(String(price)))
    return isNullOrUndefined(empty) ? null : <chakra.span>{empty}</chakra.span>

  return (
    <chakra.span fontWeight="normal" whiteSpace="nowrap">
      {formatPrice(intl, price, currency)}
    </chakra.span>
  )
}

type RangeProps = {
  min?: number | null
  max?: number | null
  currency?: LocalRefLabel | null
  dft?: string
}

export const FormattedPriceRange: React.FC<RangeProps> = ({ min, max, currency, dft }) => {
  const intl = useIntl()
  const hasMin = !isNullOrUndefined(min)
  const hasMax = !isNullOrUndefined(max)

  if (!hasMin && !hasMax) return <FormattedPrice currency={currency} dft={dft} />
  if (hasMin && hasMax && min === max) return <FormattedPrice price={min} currency={currency} />

  const values = {
    min: hasMin ? formatPrice(intl, min, currency) : "",
    max: hasMax ? formatPrice(intl, max, currency) : "",
  }
  const id = hasMin && hasMax ? "between_min_and_max" : hasMin ? "more_than" : "less_than"

  return <chakra.span fontWeight="normal">{intl.formatMessage({ id }, values)}</chakra.span>
}
